import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { CreateMemoData, ChecklistItem } from '../../services/memoService';
import { Plus, X, CheckSquare, FileText } from 'lucide-react';

interface MemoFormProps {
  availableColors: string[];
  onSubmit: (memoData: CreateMemoData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<CreateMemoData>;
  supportChecklist?: boolean;
}

export default function MemoForm({ availableColors, onSubmit, onCancel, initialData, supportChecklist = false }: MemoFormProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [title, setTitle] = useState(initialData?.title || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [color, setColor] = useState(initialData?.color || 'yellow');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memoType, setMemoType] = useState<'note' | 'checklist'>(initialData?.type || 'note');
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(
    initialData?.checklistItems || []
  );
  const [showColorPicker, setShowColorPicker] = useState(false);
  const checklistContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new checklist item is added
  useEffect(() => {
    if (memoType === 'checklist' && checklistContainerRef.current) {
      checklistContainerRef.current.scrollTop = checklistContainerRef.current.scrollHeight;
    }
  }, [checklistItems.length, memoType]);

  // Color styles for different memo colors
  const colorStyles: Record<string, { light: string, dark: string }> = {
    yellow: { light: 'bg-yellow-100', dark: 'bg-yellow-900' },
    blue: { light: 'bg-blue-100', dark: 'bg-blue-900' },
    green: { light: 'bg-green-100', dark: 'bg-green-900' },
    pink: { light: 'bg-pink-100', dark: 'bg-pink-900' },
    purple: { light: 'bg-purple-100', dark: 'bg-purple-900' },
    orange: { light: 'bg-orange-100', dark: 'bg-orange-900' },
    red: { light: 'bg-red-100', dark: 'bg-red-900' },
    teal: { light: 'bg-teal-100', dark: 'bg-teal-900' },
    indigo: { light: 'bg-indigo-100', dark: 'bg-indigo-900' },
    cyan: { light: 'bg-cyan-100', dark: 'bg-cyan-900' },
    lime: { light: 'bg-lime-100', dark: 'bg-lime-900' },
    rose: { light: 'bg-rose-100', dark: 'bg-rose-900' },
    amber: { light: 'bg-amber-100', dark: 'bg-amber-900' },
    emerald: { light: 'bg-emerald-100', dark: 'bg-emerald-900' },
    violet: { light: 'bg-violet-100', dark: 'bg-violet-900' },
    sky: { light: 'bg-sky-100', dark: 'bg-sky-900' },
  };

  // Add new checklist item
  const addChecklistItem = () => {
    const newItem: ChecklistItem = {
      id: Date.now().toString(),
      text: '',
      completed: false
    };
    setChecklistItems([...checklistItems, newItem]);
  };

  // Update checklist item
  const updateChecklistItem = (id: string, text: string) => {
    setChecklistItems(items => 
      items.map(item => item.id === id ? { ...item, text } : item)
    );
  };

  // Remove checklist item
  const removeChecklistItem = (id: string) => {
    setChecklistItems(items => items.filter(item => item.id !== id));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!title.trim()) {
      setError(t('memo.titleRequired'));
      return;
    }
    
    // For note memos, require content. For checklists, require at least one item
    if (memoType === 'note' && !content.trim()) {
      setError(t('memo.contentRequired'));
      return;
    }

    if (memoType === 'checklist' && checklistItems.filter(item => item.text.trim()).length === 0) {
      setError('At least one checklist item is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const memoData: CreateMemoData = {
        title,
        content: memoType === 'note'
          ? content
          : checklistItems.filter(item => item.text.trim()).map(item => `• ${item.text}`).join('\n') || 'Checklist',
        color,
        type: memoType,
        // Generate random position for new memos
        positionX: Math.floor(Math.random() * 400),
        positionY: Math.floor(Math.random() * 200),
      };

      // Add checklist items if it's a checklist memo
      if (memoType === 'checklist') {
        memoData.checklistItems = checklistItems.filter(item => item.text.trim());
      }
      
      await onSubmit(memoData);
    } catch (err) {
      console.error('Error submitting memo:', err);
      setError(t('memo.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className={`rounded-xl p-3.5 backdrop-blur-sm border animate-in slide-in-from-top-2 duration-300 flex items-start space-x-3 ${
          theme === 'dark'
            ? 'bg-red-500/10 text-red-300 border-red-500/30'
            : 'bg-red-50 border-red-200 text-red-700'
        }`} role="alert">
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-white text-xs font-bold">!</div>
          <span className="block font-medium text-sm">{error}</span>
        </div>
      )}
      
      {/* Title Input - Clean and Prominent */}
      <div>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={`w-full px-4 py-3 border-2 rounded-xl transition-all text-lg font-semibold placeholder:font-normal ${
            theme === 'dark' 
              ? 'bg-gray-700/30 border-gray-600 text-white placeholder-gray-500 focus:bg-gray-700/50 focus:border-blue-500' 
              : 'bg-gray-50/50 border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-400'
          } focus:outline-none focus:ring-4 focus:ring-blue-500/10 hover:border-gray-400 dark:hover:border-gray-500`}
          placeholder="Title"
          autoFocus
        />
      </div>
      
      {/* Memo Type Selector - Sleek Pills */}
      {supportChecklist && (
        <div className="flex items-center justify-center">
          <div className={`inline-flex p-1 rounded-xl ${
            theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
          }`}>
            <button
              type="button"
              onClick={() => setMemoType('note')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                memoType === 'note'
                  ? theme === 'dark'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-blue-600 shadow-md'
                  : theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText size={16} />
              <span>Note</span>
            </button>
            <button
              type="button"
              onClick={() => setMemoType('checklist')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                memoType === 'checklist'
                  ? theme === 'dark'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-blue-600 shadow-md'
                  : theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CheckSquare size={16} />
              <span>Checklist</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Content or Checklist - Cleaner Design */}
      {memoType === 'note' ? (
        <div>
          <textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl resize-none transition-all ${
              theme === 'dark' 
                ? 'bg-gray-700/30 border-gray-600 text-white placeholder-gray-500 focus:bg-gray-700/50 focus:border-blue-500' 
                : 'bg-gray-50/50 border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-400'
            } focus:outline-none focus:ring-4 focus:ring-blue-500/10 hover:border-gray-400 dark:hover:border-gray-500`}
            rows={6}
            placeholder="Take a note..."
          />
        </div>
      ) : (
        <div>
          <div 
            ref={checklistContainerRef}
            className="space-y-2 max-h-80 overflow-y-auto pr-1 custom-scrollbar scroll-smooth"
          >
            {checklistItems.map((item, index) => (
              <div key={item.id} className={`flex items-center gap-2 group p-2 rounded-lg transition-colors ${
                theme === 'dark' ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'
              }`}>
                <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                  theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                }`}>
                  <span className="text-xs text-gray-400">{index + 1}</span>
                </div>
                <input
                  type="text"
                  value={item.text}
                  onChange={(e) => updateChecklistItem(item.id, e.target.value)}
                  placeholder="List item"
                  className={`flex-1 px-3 py-2 border rounded-lg transition-all text-sm ${
                    theme === 'dark' 
                      ? 'bg-gray-700/30 border-gray-600 text-white placeholder-gray-500 focus:bg-gray-700/50 focus:border-blue-500' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                />
                <button
                  type="button"
                  onClick={() => removeChecklistItem(item.id)}
                  className={`opacity-0 group-hover:opacity-100 p-1.5 rounded-md transition-all hover:scale-110 ${
                    theme === 'dark' 
                      ? 'hover:bg-red-600/20 text-red-400 hover:text-red-300' 
                      : 'hover:bg-red-50 text-red-500 hover:text-red-600'
                  }`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addChecklistItem}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Plus size={16} />
              <span>Add list item</span>
            </button>
            {checklistItems.length === 0 && (
              <div className={`text-center py-6 px-4 rounded-xl border-2 border-dashed ${
                theme === 'dark' ? 'border-gray-700 bg-gray-800/20' : 'border-gray-200 bg-gray-50/50'
              }`}>
                <CheckSquare className={`mx-auto mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} size={28} />
                <p className={`text-xs ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  Click "Add list item" to create your checklist
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Color Picker - Google Keep Style */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowColorPicker(!showColorPicker)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            theme === 'dark' 
              ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <div 
            className={`w-5 h-5 rounded-full border-2 ${
              colorStyles[color][theme === 'dark' ? 'dark' : 'light']
            } ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}
          ></div>
          <span>Background color</span>
        </button>
        
        {showColorPicker && (
          <div className={`absolute left-0 top-full mt-2 p-3 rounded-xl shadow-xl border z-10 ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex flex-wrap gap-1.5 max-w-xs">
              {availableColors.map((colorOption) => (
                <button
                  key={colorOption}
                  type="button"
                  onClick={() => {
                    setColor(colorOption);
                    setShowColorPicker(false);
                  }}
                  className={`w-6 h-6 rounded-full transition-all hover:scale-110 relative ${
                    colorStyles[colorOption][theme === 'dark' ? 'dark' : 'light']
                  } ${
                    color === colorOption
                      ? 'ring-2 ring-blue-500 ring-offset-2 scale-105'
                      : 'hover:ring-2 hover:ring-gray-400'
                  }`}
                  title={colorOption.charAt(0).toUpperCase() + colorOption.slice(1)}
                >
                  {color === colorOption && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full shadow-lg"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Action Buttons - Bottom Aligned */}
      <div className={`flex justify-end gap-3 pt-5 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
        <button
          type="button"
          onClick={onCancel}
          className={`px-5 py-2.5 rounded-lg font-medium transition-all text-sm ${
            theme === 'dark' 
              ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`px-6 py-2.5 rounded-lg font-medium transition-all shadow-lg flex items-center gap-2 ${
            theme === 'dark' 
              ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-600/20 hover:shadow-blue-600/40' 
              : 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-500/20 hover:shadow-blue-500/40'
          } disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]`}
          disabled={isSubmitting}
        >
          {isSubmitting && (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          )}
          <span>{isSubmitting ? 'Creating...' : 'Create Note'}</span>
        </button>
      </div>
    </form>
  );
}
